<script setup lang="ts">
    import { inject, onMounted, reactive, ref, watch  } from 'vue';
    import axios from 'axios';
    import { useRoute } from 'vue-router';
    import CustomUploadInput from '@/components/CustomUploadInput.vue';

    const route = useRoute();
    const BACKEND_URL = inject('BACKEND_URL');
    const errorMessage = ref('');
    const successMessage = ref('');
    const loading = ref(false);
    const isFormValid = ref(false);
    const AVATARS_LIST = ['user.jpg', 'user1.jpg', 'user2.jpg', 'user3.jpg', 'user4.jpg', 'user5.jpg', 'user6.jpg', 'user7.jpg', 'user8.jpg', 'user9.jpg', 'user10.jpg', 'user11.jpg'];
    const formData = reactive({
        id: null,
        avatar: AVATARS_LIST.length > 0 ? AVATARS_LIST[0] : '',
        role: '',
        username: '',
        email: '',
        photo: null as any,
        photo_preview: '/assets/users/default.png',
        sexe: 'M',
        nom: '',
        prenom: '',
        fonction: '',
        telephone: '',
        password: '',
        confirm_password: ''
    });
    const errors = reactive({
        email: '',
        nom: '',
        prenom: '',
        telephone: '',
        password: ''
    });

    onMounted(() => {
        const startupFormInfos: any = route.meta.form_datas;
        if (startupFormInfos !== undefined) {
            formData.id = startupFormInfos.id;
            formData.role = startupFormInfos.role;
            formData.avatar = startupFormInfos.avatar;
            formData.username = startupFormInfos.username;
            formData.email = startupFormInfos.email;
            if (startupFormInfos.photo_url) {
                formData.photo_preview = startupFormInfos.photo_url;
            }
            formData.sexe = startupFormInfos.sexe;
            formData.nom = startupFormInfos.nom && startupFormInfos.nom != 'null' ? startupFormInfos.nom : '';
            formData.prenom = startupFormInfos.prenom && startupFormInfos.prenom != 'null' ? startupFormInfos.prenom : '';
            formData.fonction = startupFormInfos.fonction && startupFormInfos.fonction != 'null' ? startupFormInfos.fonction : '';
            formData.telephone = startupFormInfos.telephone && startupFormInfos.telephone != 'null' ? startupFormInfos.telephone : '';
        }
    });

    watch(formData, (oldFormData, newFormData) => {
        validate();
    });

    function doSelectAvatar(avatar: string) {
        formData.avatar = avatar;
    }

    function validatePhone(phoneValue: string) {
        // Exemple pour format international commençant par + (ex: +2250700000000)
        const regex = /^\+?[0-9]{8,15}$/;
        return regex.test(phoneValue);
    }

    function validateEmail(emailValue: string) {
        // Exemple pour format international commençant par + (ex: +2250700000000)
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return regex.test(emailValue);
    }

    function validate() {
        let isValid = true;

        errors.email = '';
        if (!validateEmail(formData.email)) {
            errors.email ="L'adresse email est invalide";
            isValid = false;
        }

        /*errors.nom = '';
        if (!formData.nom) {
            errors.nom ="Le nom est requis";
            isValid = false;
        }

        errors.prenom = '';
        if (!formData.prenom) {
            errors.prenom ="Le prénom est requis";
            isValid = false;
        }*/

        errors.telephone = '';
        if (formData.telephone && !validatePhone(formData.telephone)) {
            errors.telephone = 'Le téléphone est invalide';
            isValid = false;
        }

        isFormValid.value = isValid;
        
        return isValid;
    }

    function onFileUploaded(uploadedFile: { file: any, file_name: string, file_preview: string }) {
        formData.photo_preview = uploadedFile.file_preview;
        formData.photo = { file: uploadedFile.file, name: uploadedFile.file_name };
    }

    async function onSubmit() {
        if (!validate()) { return; }
        loading.value = true;
        errorMessage.value = '';
        successMessage.value = '';
        try {

            const formToUpload = new FormData();
            if (formData.id != null) {
                formToUpload.append("id", `${formData.id}`);
            }
            formToUpload.append("avatar", formData.avatar);
            formToUpload.append("email", formData.email);
            formToUpload.append("password", formData.password);
            formToUpload.append("confirm_password", formData.confirm_password);
            if (formData.photo?.file) {
                formToUpload.append("photo", formData.photo.file, formData.photo.name);
            }
            formToUpload.append("sexe", formData.sexe);
            formToUpload.append("nom", formData.nom);
            formToUpload.append("prenom", formData.prenom);
            formToUpload.append("fonction", formData.fonction);
            formToUpload.append("telephone", formData.telephone);

            await axios.post(`/backoffice/auth/update_account_datas`, formToUpload);
            successMessage.value = 'Modifications enregistrées !';
            
        } catch(error: any) {
            errorMessage.value = error.message;
            console.error(error);
        } finally {
            loading.value = false;
        }
    }
</script>

<style>
.alert-success {
  color: #155724;
  background-color: #d4edda;
  border-color: #c3e6cb;
}
.f-w-500 { font-weight: 500; }
</style>

<template>
<section class="container-full-1 bg-grey-50">
        <div class="page-space-40"></div>

        <form class="form widget flex container-full-3 py-8 pb-10">
            <section class="s9_mobile">
                <h4 class="font-semibold mb-5">Informations du compte <span class="text-sm text-grey-500"></span></h4>
                <div class="h-20"></div>

                <div class="grid6 grid-row-gap-1">
                    <div class="rounded-100 overflow-hidden cursor-pointer item" @click="doSelectAvatar(avatar)" :class="{'active': formData.avatar == avatar }" v-for="(avatar, index) in AVATARS_LIST" :key="'avatar_' + index"><!--active-->
                        <img :src="'/assets/users/300w/' + avatar" width="100%" />
                    </div>
                </div>

                <div class="form-field-floating reactive my-4">
                    <input type="text" class="form-input" :value="formData.role" name="role_id" id="role_id" placeholder="" readonly />
                    <label for="role_id">Rôle <b class="color-danger">*</b></label>
                </div>

                <div class="form-field-floating reactive my-4">
                    <input type="text" class="form-input" :value="formData.username" name="username" id="username" placeholder="" readonly />
                    <label for="username">Identifiant <b class="color-danger">*</b></label>
                </div>

                <div class="form-field-floating reactive my-4">
                    <input type="email" class="form-input" v-model="formData.email" name="email" id="email" placeholder="">
                    <label for="email">Email <b class="color-danger">*</b></label>
                </div>
                <span v-if="!!errors['email']" class="text-red">{{ errors['email'] }}</span>

                <div class="form-field-floating reactive my-4">
                    <input type="password" class="form-input" v-model="formData.password" name="password" id="password" placeholder="">
                    <label for="password">Mot de passe </label>
                </div>

                <div class="form-field-floating reactive my-4">
                    <input type="password" class="form-input" v-model="formData.confirm_password" name="confirm_password" id="confirm_password" placeholder="">
                    <label for="confirm_password">Confirmer Mot de passe </label>
                </div>
                <span v-if="!!errors['password']" class="text-red">{{ errors['password'] }}</span>
            </section>

            <div class="s1_mobile flex-center">
                <hr class="hr-vertical h-90p"/>
            </div>

            <section class="s10_mobile">
                <h4 class="font-semibold mb-5">Informations personnelles <span class="text-sm text-grey-500"></span></h4>


                <div class="h-20"></div>
                <div class="custom-upload-size margin-auto flex mb-8">
                    <custom-upload-input id="photo" label="Photo de profil" :preview="formData.photo_preview" type="IMAGE" @uploaded="onFileUploaded"></custom-upload-input>
                </div>
                <div class="grid2">
                    <div class="card-check mb-2" :class="{ 'active': formData.sexe == 'M' }"><!-- color active sm single -->
                        <label class="form-check-label" for="sexe_M">
                            <div class="card-check-icon"><img src="/icons/icon-male.svg" /></div>
                            <div class="card-check-text">
                                <input class="form-check-input" type="radio" name="sexe" id="sexe_M" value="M" v-model="formData.sexe" />
                                <h6>Je suis un homme</h6>
                                <span>Masculin</span>
                            </div>  
                        </label>
                    </div>
                    <div class="card-check mb-2" :class="{ 'active': formData.sexe == 'F' }"><!-- color active sm single -->
                        <label class="form-check-label" for="sexe_F">
                            <div class="card-check-icon"><img src="/icons/icon-female.svg" /></div>
                            <div class="card-check-text">
                                <input class="form-check-input" type="radio" name="sexe" id="sexe_F" value="F" v-model="formData.sexe" />
                                <h6>Je suis une femme</h6>
                                <span>Féminin</span>
                            </div>  
                        </label>
                    </div>
                </div>

                <div class="form-field-floating reactive my-4">
                    <input type="text" class="form-input" v-model="formData.nom" name="nom" id="nom" placeholder="">
                    <label for="nom">Nom</label>
                </div>
                <span v-if="!!errors['nom']" class="text-red">{{ errors['nom'] }}</span>

                <div class="form-field-floating reactive my-4">
                    <input type="text" class="form-input" v-model="formData.prenom" name="prenom" id="prenom" placeholder="">
                    <label for="prenom">Prénom</label>
                </div>
                <span v-if="!!errors['prenom']" class="text-red">{{ errors['prenom'] }}</span>

                <div class="form-field-floating reactive my-4">
                    <input type="text" class="form-input" v-model="formData.fonction" name="fonction" id="fonction" placeholder="">
                    <label for="fonction">Fonction</label>
                </div>

                <div class="form-field-floating reactive my-4">
                    <input type="text" class="form-input" v-model="formData.telephone" name="telephone" id="telephone" placeholder="">
                    <label for="telephone">Téléphone</label>
                </div>
                <span v-if="!!errors['telephone']" class="text-red">{{ errors['telephone'] }}</span>
                
                <div class="clear-both h-25"></div>
                <div v-if="!!successMessage" class="alert-success py-5 px-5 f-w-500" style="border-radius: 5px">{{ successMessage }}</div>
                <span v-if="!!errorMessage" class="text-red">{{ errorMessage }}</span>
                <p class="text-xs text-grey-500 mb-5"><b class="color-danger text-xs">*</b> Les champs marqués d'un astérisque sont obligatoires</p>
                <div class="flex gap-2">
                    <custom-button id="btn-submit" type="button" @click="onSubmit()" :classesList="'btn lg w-100'" :loading="loading">
                        <img src="/icons/icon-enregistrer.svg" class="btn-icon" alt="" />
                        <span>Enregistrer</span>
                    </custom-button>
                </div>
            </section>
        </form>
    </section>
</template>
